import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { listConsumableBalances, adjustConsumableBalance, loadCredits, getLedgerHistory } from '../api/consumableBalances'
import { getLiveCampaignEventCounts } from '@/features/developer-console/api/balanceInspector'
import type { AdjustBalanceInput, LoadCreditsInput } from '../types/consumableBalance.types'

/**
 * Same floor ConsumableBalanceController falls back to when an account has no createdAt on
 * file — harmless as a lower bound either way, since campaign_events has no rows before an
 * account existed regardless of how far back the query's start date reaches. Used here so the
 * Load Credits preview shows the same "live consumed" figure the backend will actually compute.
 */
const LIFETIME_CONSUMED_FLOOR = '2020-01-01'

export function useConsumableBalances(accountId: string | null) {
  return useQuery({
    queryKey: ['consumable-balances', accountId],
    queryFn: () => listConsumableBalances(accountId!),
    enabled: !!accountId,
  })
}

export function useAdjustBalance(accountId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: AdjustBalanceInput) => adjustConsumableBalance(accountId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumable-balances', accountId] })
      queryClient.invalidateQueries({ queryKey: ['consumable-ledger', accountId] })
    },
  })
}

export function useLoadCredits(accountId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: LoadCreditsInput) => loadCredits(accountId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumable-balances', accountId] })
      queryClient.invalidateQueries({ queryKey: ['consumable-ledger', accountId] })
    },
  })
}

/** Live (lifetime-to-date) consumption preview for the Load Credits modal — the same figure the
 *  backend's /load endpoint will itself compute and lock in as the new totalConsumed. */
export function useLifetimeConsumedPreview(accountId: string | null) {
  const today = new Date().toISOString().slice(0, 10)
  return useQuery({
    queryKey: ['lifetime-consumed-preview', accountId],
    queryFn: () => getLiveCampaignEventCounts(accountId!, LIFETIME_CONSUMED_FLOOR, today),
    enabled: !!accountId,
  })
}

export function useLedgerHistory(accountId: string | null, channel: string | undefined, page = 0) {
  return useQuery({
    queryKey: ['consumable-ledger', accountId, channel, page],
    queryFn: () => getLedgerHistory(accountId!, channel, page),
    enabled: !!accountId,
  })
}
